import {
  DatabaseAuto, IcommerceUser, IdataArrayResponse, IecommerceOrder,
  IfilterProps,
  Iitem,
  IitemReturn,
  TitemReturnRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

/**
 * Type alias for item return properties with specific generic types
 * @template T - Type for order (default: IecommerceOrder<number, IcommerceUser>)
 * @template C - Type for company (default: Icompany)
 * @template U - Type for user (default: IcommerceUser)
 * @template I - Type for item (default: Iitem)
 */
type TitemReturnProps = IitemReturn<IecommerceOrder<number, IcommerceUser>, string, IcommerceUser, Iitem>;

/**
 * Represents an item return for a specific order.
 * Extends DatabaseAuto to inherit common database operations.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link ItemReturn} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const itemReturn = await ItemReturn.getAll();
 *
 * const itemReturn = await ItemReturn.filterAll({
 *
 * });
 *
 * const itemReturn = await ItemReturn.bulkDelete({
 *
 * });
 *
 * const itemReturn = await ItemReturn.getOne('resourceId');
 *
 * const itemReturn = await ItemReturn.remove();
 *
 * ```
 */
export class ItemReturn
  extends DatabaseAuto {
  /** Base URL for the item return API endpoint */
  private static readonly baseUrl = '/item-return';

  /** User associated with the return */
  readonly commerceUser: TitemReturnProps['commerceUser'];

  /** Order associated with the return */
  readonly ecommerceOrder: TitemReturnProps['ecommerceOrder'];

  /** Items to be returned */
  readonly items: TitemReturnProps['items'];

  /** Reason for the return */
  readonly returnReason: TitemReturnProps['returnReason'];

  /** Status of the return */
  readonly status: TitemReturnProps['status'];
  readonly ecommerceVendors: TitemReturnProps['ecommerceVendors'];

  /**
   * Constructs an instance of `ItemReturn`.
   *
   * @param {TitemReturnProps} data - The itemReturn data used to initialize the instance.
   * @throws {Error} Throws an error if no data is provided.
   */
  constructor(data: TitemReturnProps) {
    super(data);

    this.commerceUser = data.commerceUser;
    this.ecommerceOrder = data.ecommerceOrder;
    this.items = data.items;
    this.returnReason = data.returnReason;
    this.status = data.status;
    this.ecommerceVendors = data.ecommerceVendors;
  }

  /**
   * Retrieves all itemreturns with pagination.
   *
   * @param {number} offset - The starting point for the retrieval.
   * @param {number} limit - The number of records to retrieve.
   * @returns {Promise<{ count: number; dataArr: ItemReturn[] }>}
   * A promise resolving to an object containing count and data array.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TitemReturnProps>>(`${ItemReturn.baseUrl}?${query}`);
    const itemReturns = await lastValueFrom(observer$);

    return {
      count: itemReturns.count,
      dataArr: itemReturns.data.map((val) => new ItemReturn(val))
    };
  }

  /**
   * Filters itemreturns based on the provided criteria.
   *
   * @param {IfilterProps} filter - The filter criteria.
   * @returns {Promise<{ count: number; dataArr: ItemReturn[] }>} A promise resolving
   * to an object containing count and data array.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TitemReturnProps>>(
        `${ItemReturn.baseUrl}/filter`,
        filter
      );
    const itemReturns = await lastValueFrom(observer$);

    return {
      count: itemReturns.count,
      dataArr: itemReturns.data.map((val) => new ItemReturn(val))
    };
  }

  /**
   * Retrieves a single itemreturn by resource ID.
   *
   * @param {string} resourceId - The ID of the itemreturn to retrieve.
   * @returns {Promise<ItemReturn>} The retrieved `ItemReturn` instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TitemReturnProps>(`${ItemReturn.baseUrl}/${resourceId}`);
    const itemReturn = await lastValueFrom(observer$);

    return new ItemReturn(itemReturn);
  }

  /**
   * Creates a new item return with the provided data.
   *
   * @param {TitemReturnRequest<true>} payload - The return data to create
   * @returns {Promise<ItemReturn>} The newly created ItemReturn instance
   * @throws {Error} When the creation fails due to invalid data or API error
   *
   * @example
   * const newReturn = await ItemReturn.add({
   *   orderId: 'order123',
   *   items: [{
   *     itemId: 'item456',
   *     quantity: 1,
   *     reason: 'Defective product'
   *   }],
   *   returnReason: 'Quality issues',
   *   status: 'pending'
   * });
   */
  static async add(payload: TitemReturnRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TitemReturnProps>(
        `${ItemReturn.baseUrl}`,
        payload
      );

    return new ItemReturn(await lastValueFrom(observer$));
  }

  /**
   * Updates the current item return with new data.
   *
   * @param {TitemReturnRequest} payload - The updated return data
   * @returns {Promise<ItemReturn>} The updated ItemReturn instance
   * @throws {Error} When the update fails or payload is invalid
   *
   * @example
   * const itemReturn = await ItemReturn.getOne('return123');
   * const updated = await itemReturn.update({
   *   status: 'processed',
   *   processedDate: new Date()
   * });
   */
  async update(payload: TitemReturnRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<TitemReturnProps>(
        `${ItemReturn.baseUrl}/${this._id}`,
        payload
      );

    return new ItemReturn(await lastValueFrom(observer$));
  }
}
